import { createAuthClient } from 'better-auth/react'
import { apiKeyClient } from 'better-auth/client/plugins'

import { redirect } from '@tanstack/react-router'

import { getApiUrl } from './api'

export const auth = createAuthClient({
  baseURL: getApiUrl(),
  basePath: '/api/auth',
  plugins: [apiKeyClient()],
})

// Infer the full session type (includes session + user)
export type Session = typeof auth.$Infer.Session

// Derive the user type from the session
export type User = Session['user']

/**
 * Sanitizes a path for use as a post-login redirect.
 * Only allows safe relative paths (e.g. /dashboard). Rejects absolute URLs,
 * protocol-relative URLs, and paths with schemes.
 */
function sanitizeRedirectPath(path: string): string {
  if (!path || typeof path !== 'string') {
    return '/'
  }
  let decoded = path.trim()
  try {
    decoded = decodeURIComponent(decoded)
  } catch {
    return '/'
  }
  // Reject protocol-relative URLs (//example.com)
  if (decoded.startsWith('//')) {
    return '/'
  }
  // Reject absolute URLs with scheme (https://, javascript:, etc.)
  if (decoded.includes('://')) {
    return '/'
  }
  // Must start with single '/' for safe relative path from app root
  if (!decoded.startsWith('/')) {
    return '/'
  }
  return decoded
}

export async function requireAuth(currentPath: string) {
  const { data: session, error } = await auth.getSession()

  if (error) {
    console.error('Authentication check failed:', error)
  }

  if (!session) {
    const safeRedirect = sanitizeRedirectPath(currentPath)
    throw redirect({
      to: '/sign-in',
      search: {
        redirect: safeRedirect,
      },
    })
  }
  return session
}
